import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Line, ComposedChart,
} from "recharts";
import { TrendingUp, TrendingDown, Wallet, ChevronRight } from "lucide-react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// FIN-1 — FluxoCaixaChart
// BarChart 6m (Entradas, Saídas) com linha dotted de Economia.
// Tooltip BR formatado. Mini-cards no header com 3 KPIs do mês atual
// + delta_pct comparado ao mês anterior.

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL", maximumFractionDigits: 0,
});

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-surface-overlay border border-subtle px-3 py-2 shadow-lg backdrop-blur-md">
      <p className="text-caption font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-micro tabular-nums" style={{ color: p.color }}>
          {p.name}: {fmtBRL(p.value)}
        </p>
      ))}
    </div>
  );
}

function MiniKpi({ icon: Icon, label, value, deltaPct, tone }) {
  const TONE = {
    emerald: "text-brand-green",
    error: "text-status-error",
    cyan: "text-brand-cyan",
  };
  const isPos = (deltaPct ?? 0) >= 0;
  return (
    <div className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-subtle bg-surface-card/40">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={cn("w-3.5 h-3.5", TONE[tone])} aria-hidden="true" />
        <span className="text-micro text-foreground-subtle uppercase" style={{ letterSpacing: "0.08em" }}>
          {label}
        </span>
      </div>
      <p className={cn("text-base font-bold tabular-nums", TONE[tone])}>{fmtBRL(value)}</p>
      {deltaPct !== undefined && (
        <p className={cn("text-micro tabular-nums", isPos ? "text-brand-green" : "text-status-error")}>
          {isPos ? "↑" : "↓"} {Math.abs(deltaPct)}% vs mês anterior
        </p>
      )}
    </div>
  );
}

export default function FluxoCaixaChart({ cashflow, loading, onSeeDetails }) {
  const months = cashflow?.months || [];
  const cur = cashflow?.current_month || { income: 0, expense: 0, savings: 0 };

  const chartData = useMemo(() =>
    months.map(m => ({
      label: m.label,
      Entradas: m.income,
      Saídas: m.expense,
      Economia: m.savings,
    })),
  [months]);

  const hasData = months.some(m => m.income > 0 || m.expense > 0);

  return (
    <Card variant="elevated" className="glass-card glow-hover p-5 md:p-6 flex flex-col" data-testid="fluxo-caixa-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <span className="text-caption font-semibold uppercase text-brand-cyan/80 inline-flex items-center gap-2" style={{ letterSpacing: "0.12em" }}>
          <span className="w-1.5 h-1.5 rounded-full bg-brand-cyan" aria-hidden="true" />
          Fluxo de Caixa · 6m
        </span>
        <button
          type="button"
          onClick={onSeeDetails}
          className="inline-flex items-center gap-1 text-caption text-brand-cyan hover:text-brand-cyan/80 transition-colors"
          data-testid="fluxo-see-details"
        >
          Ver detalhes
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <MiniKpi icon={TrendingUp} label="Entrada" value={cur.income} deltaPct={cur.income_delta_pct} tone="emerald" />
        <MiniKpi icon={TrendingDown} label="Saída" value={cur.expense} deltaPct={cur.expense_delta_pct} tone="error" />
        <MiniKpi icon={Wallet} label="Economia" value={cur.savings} deltaPct={cur.savings_delta_pct} tone={cur.savings >= 0 ? "emerald" : "error"} />
      </div>

      {loading ? (
        <div className="flex-1 h-[260px] rounded-lg bg-surface-card-hover/40 animate-pulse" />
      ) : !hasData ? (
        <div className="flex-1 flex items-center justify-center py-8 text-center">
          <p className="text-sm text-foreground-muted max-w-xs">
            Sem histórico ainda. Registre transações pra ver evolução mês a mês.
          </p>
        </div>
      ) : (
        <div className="flex-1 min-h-[260px]" data-testid="fluxo-chart">
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis
                dataKey="label"
                stroke="#64748B"
                tick={{ fill: "#94A3B8", fontSize: 11 }}
                axisLine={false} tickLine={false}
              />
              <YAxis
                stroke="#64748B"
                tick={{ fill: "#94A3B8", fontSize: 11 }}
                axisLine={false} tickLine={false}
                tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,212,255,0.04)" }} />
              <Legend
                iconType="circle" iconSize={8}
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              />
              <Bar dataKey="Entradas" fill="#00FFA3" radius={[4, 4, 0, 0]} barSize={12} />
              <Bar dataKey="Saídas" fill="#FF4D6D" radius={[4, 4, 0, 0]} barSize={12} />
              <Line
                type="monotone" dataKey="Economia" stroke="#FFC857"
                strokeWidth={2} strokeDasharray="4 4" dot={{ fill: "#FFC857", r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
